import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {
    systemSetPopupMessage,
    systemSetPushSubscription,
    systemSetPushSubscriptionBrowserState,
    systemSetPushSubscriptionUserState,
} from '../actions';
import { pushSubscription2FuntipPushSubscription } from '../sagas';
import {
    systemPushSubscriptionBrowserStateSelector,
    systemPushSubscriptionDeniedByUserDateSelector,
    systemPushSubscriptionEnabledByUserSelector,
    systemPushSubscriptionSelector,
    userIdSelector,
} from '../selectors';
import { getProfile2Api, getPushNotification2Api } from '../systemConfig';

export type PushSubscriptionState = 'granted' | 'denied' | 'noServiceWorker' | 'unknown' | 'notLoggedIn' | 'default';

export const PushNotificationAskForPermission: React.FC = () => {
    const pushSubscriptionEnabledByUser = useSelector(systemPushSubscriptionEnabledByUserSelector);
    const pushSubscriptionDeniedByUserDate = useSelector(systemPushSubscriptionDeniedByUserDateSelector);
    const pushSubscriptionBrowserState = useSelector(systemPushSubscriptionBrowserStateSelector);
    const pushSubscription = useSelector(systemPushSubscriptionSelector);

    const [showDialog, setShowDialog] = React.useState(false);

    const dispatch = useDispatch();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const loggedInUserId = useSelector(userIdSelector);

    const publicKeyQuery = useQuery({
        queryKey: ['publicKeyQuery'],
        queryFn: () => getPushNotification2Api().getPublicKey(),
        refetchOnWindowFocus: false,
    });

    const pushNotificationStateQuery = useQuery({
        queryKey: ['pushNotificationState', loggedInUserId],
        queryFn: () => getProfile2Api().getUserPushNotificationState(),
        enabled: !!loggedInUserId
    });

    const allowMutation = useMutation({
        mutationFn: () => getProfile2Api().allowUserPushNotification(),
    });

    const denyMutation = useMutation({
        mutationFn: () => getProfile2Api().denyUserPushNotification(),
    });

    const { mutate: subscribeMutation } = useMutation({
        mutationFn: (pushSubscription: PushSubscription) =>
            getPushNotification2Api().subscribe({
                funtipPushSubscription2: pushSubscription2FuntipPushSubscription(pushSubscription!),
            }),
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { mutate: unsubscribeMutation } = useMutation({
        mutationFn: (endpoint: string) => getPushNotification2Api().unsubscribe({ endpoint }),
    });

    const showPushNotificationAskForPermissionDialog = useCallback(() => {
        const lastDeniedDate = pushSubscriptionDeniedByUserDate ?? DateTime.invalid('null value');
        return (
            (pushSubscriptionEnabledByUser ||
                (!pushSubscriptionEnabledByUser &&
                    (!lastDeniedDate.isValid ||
                        (lastDeniedDate.isValid &&
                            lastDeniedDate.plus({ days: 30 }).toMillis() < DateTime.now().toMillis())))) &&
            ['unknown', 'denied', 'default'].includes(pushSubscriptionBrowserState)
        );
    }, [pushSubscriptionBrowserState, pushSubscriptionDeniedByUserDate, pushSubscriptionEnabledByUser]);

    useEffect(() => {
        if (!!loggedInUserId && !!pushNotificationStateQuery.data) {
            dispatch(
                systemSetPushSubscriptionUserState({
                    pushSubscriptionEnableByUser: !pushNotificationStateQuery.data.pushNotificationsDenied,
                    pushSubscriptionDeniedByUserDate: !!pushNotificationStateQuery.data.pushNotificationsDeniedDate
                        ? DateTime.fromJSDate(pushNotificationStateQuery.data.pushNotificationsDeniedDate)
                        : undefined,
                }),
            );
        }
    }, [dispatch, loggedInUserId, pushNotificationStateQuery.data]);

    useEffect(() => {
        if (!!loggedInUserId) {
            if ('Notification' in window && 'serviceWorker' in navigator) {
                dispatch(systemSetPushSubscriptionBrowserState(Notification.permission));
                console.log(`Notification permission ${Notification.permission}`);
            } else {
                dispatch(systemSetPushSubscriptionBrowserState('noServiceWorker'));
            }
        } else {
            dispatch(systemSetPushSubscriptionBrowserState('notLoggedIn'));
        }
    }, [dispatch, loggedInUserId]);

    useEffect(() => {
        setShowDialog(showPushNotificationAskForPermissionDialog());
    }, [showPushNotificationAskForPermissionDialog]);

    const tryToSubscribe = async () => {
        if ('Notification' in window && 'serviceWorker' in navigator && publicKeyQuery?.data?.message) {
            console.info('Trying to subscribe');
            try {
                const registration = await navigator.serviceWorker.ready;
                console.info('Service worker ready');

                const permission = await Notification.requestPermission();
                dispatch(systemSetPushSubscriptionBrowserState(permission));

                if (permission !== 'granted') {
                    const subscription: PushSubscription = await registration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: publicKeyQuery.data.message,
                    });
                    console.info('Push subscription: ', subscription);
                    dispatch(systemSetPushSubscription(subscription));
                }
            } catch (e) {
                console.error('Error subscribing: ' + JSON.stringify(e));
            }
        }
    };

    useEffect(() => {
        // subscribe if user is logged in and precondition are met
        if (
            loggedInUserId &&
            pushSubscriptionEnabledByUser &&
            pushSubscriptionBrowserState === 'granted' &&
            pushSubscription
        ) {
            subscribeMutation(pushSubscription);
        }

        // unsubscribe if user is logged out and push subscription is enabled in the browser
        if (!loggedInUserId && pushSubscriptionBrowserState === 'granted' && pushSubscription?.endpoint) {
            unsubscribeMutation(pushSubscription.endpoint);
        }
    }, [
        loggedInUserId,
        pushSubscription,
        pushSubscriptionBrowserState,
        pushSubscriptionEnabledByUser,
        subscribeMutation,
        unsubscribeMutation,
    ]);

    const onAllow = () => {
        tryToSubscribe();
        allowMutation.mutate(undefined, {
            onSuccess: () => {
                pushNotificationStateQuery.refetch();
            },
            onError: (err) => {
                dispatch(systemSetPopupMessage('Fehler beim Speichern der Einstellungen', err));
            },
        });
        setShowDialog(false);
    };

    const onDeny = () => {
        denyMutation.mutate(undefined, {
            onSuccess: () => {
                pushNotificationStateQuery.refetch();
            },
            onError: (err) => {
                dispatch(systemSetPopupMessage('Fehler beim Speichern der Einstellungen', err));
            },
        });
        setShowDialog(false);
    };

    return (
        <Modal toggle={onDeny} isOpen={showDialog}>
            <ModalHeader toggle={onDeny}>Benachrichtigungen erhalten</ModalHeader>
            <ModalBody>
                <p>Wir würden uns freuen, wenn wir Sie über wichtige Neuigkeiten und Angebote informieren dürfen.</p>
                <p>Sie können die Benachrichtigungen jederzeit in den Einstellungen Ihres Browsers deaktivieren.</p>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={onDeny}>
                    Nein, danke.
                </Button>
                <Button color="success" onClick={onAllow}>
                    Ja, gerne.
                </Button>
            </ModalFooter>
        </Modal>
    );
};
